import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Wrench } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UNIT_LABEL = {
  business_days: "días laborables",
  natural_days: "días naturales",
};

export default function AppointmentServicesPage() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [centers, setCenters] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // Iter 149 — permisos del usuario actual obtenidos del backend
  const [perms, setPerms] = useState({ can_view: true, can_edit: false });

  const axiosAuth = () => axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` },
  });

  const reload = async () => {
    setLoading(true);
    try {
      const [s, c, p] = await Promise.all([
        axiosAuth().get("/admin/appointment-services?include_archived=true"),
        axiosAuth().get("/centers?active_only=true"),
        axiosAuth().get("/admin/appointment-services/permissions").catch(() => ({ data: { can_edit: false } })),
      ]);
      setItems(s.data.items || []);
      setCenters(c.data || []);
      setPerms(p.data || { can_view: true, can_edit: false });
    } catch {
      toast.error("Error cargando servicios");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const openNew = () => {
    setEditingId("new");
    setEditForm({
      name: "", slug: "", active: true, order: items.length,
      min_delay_value: 3, min_delay_unit: "business_days",
      duration_minutes: 60, centers: centers.map(c => c.slug),
      help_text: "", requires_call: false, allows_replacement_vehicle: true,
      color: "",
    });
  };

  const openEdit = (svc) => {
    setEditingId(svc.id);
    setEditForm({
      name: svc.name || "", slug: svc.slug || "",
      active: svc.active !== false,
      order: svc.order || 0,
      min_delay_value: svc.min_delay_value ?? 3,
      min_delay_unit: svc.min_delay_unit || "business_days",
      duration_minutes: svc.duration_minutes || 60,
      centers: svc.centers || [],
      help_text: svc.help_text || "",
      requires_call: !!svc.requires_call,
      allows_replacement_vehicle: svc.allows_replacement_vehicle !== false,
      color: svc.color || "",
    });
  };

  const save = async () => {
    if (!editForm.name.trim()) { toast.error("Nombre obligatorio"); return; }
    try {
      if (editingId === "new") {
        await axiosAuth().post("/admin/appointment-services", editForm);
        toast.success("Servicio creado");
      } else {
        await axiosAuth().put(`/admin/appointment-services/${editingId}`, editForm);
        toast.success("Servicio actualizado");
      }
      setEditingId(null); setEditForm(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    }
  };

  const move = async (idx, dir) => {
    const next = [...items];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= next.length) return;
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    setItems(next);
    try {
      await axiosAuth().put("/admin/appointment-services/reorder", { order: next.map(s => s.id) });
    } catch {
      toast.error("Error reordenando");
      reload();
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      const r = await axiosAuth().delete(`/admin/appointment-services/${confirmDelete.id}?confirm=ELIMINAR`);
      if (r.data?.archived) {
        toast.info(r.data.message || "Servicio archivado");
      } else {
        toast.success("Servicio eliminado");
      }
      setConfirmDelete(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error eliminando");
    }
  };

  const canEdit = perms.can_edit;

  return (
    <div className="space-y-5" data-testid="appointment-services-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="heading-industrial text-2xl uppercase font-bold text-slate-900 flex items-center gap-2">
            <Wrench className="w-6 h-6 text-red-600" /> Servicios de Cita
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Configura los servicios ofrecidos en el flujo público de reserva. Los plazos
            controlan la primera fecha disponible en el calendario.
          </p>
        </div>
        {canEdit && (
          <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm" data-testid="svc-new-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Nuevo servicio
          </Button>
        )}
      </div>

      {!canEdit && !loading && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-900 flex items-center gap-2" data-testid="svc-readonly-banner">
          <Wrench className="w-4 h-4" />
          <span>
            <strong>Modo solo lectura.</strong> Sin permisos para modificar servicios.
            Solo super_admin, admin y jefe_taller pueden crear, editar, ordenar o archivar servicios de cita.
          </span>
        </div>
      )}

      <Card className="rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-400">Cargando…</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left py-2 px-3 w-12">Orden</th>
                <th className="text-left py-2 px-3">Nombre</th>
                <th className="text-left py-2 px-3">Plazo mínimo</th>
                <th className="text-left py-2 px-3">Duración</th>
                <th className="text-left py-2 px-3">Centros</th>
                <th className="text-left py-2 px-3">Estado</th>
                <th className="text-right py-2 px-3">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((svc, idx) => (
                <tr key={svc.id} className={`border-t border-slate-100 ${!svc.active ? 'opacity-50' : ''}`} data-testid={`svc-row-${svc.slug}`}>
                  <td className="py-2 px-3">
                    {canEdit && (
                      <div className="flex flex-col gap-0.5">
                        <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-slate-400 hover:text-slate-900 disabled:opacity-30">
                          <ArrowUp className="w-3 h-3" />
                        </button>
                        <button onClick={() => move(idx, +1)} disabled={idx === items.length - 1} className="text-slate-400 hover:text-slate-900 disabled:opacity-30">
                          <ArrowDown className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    <div className="font-semibold text-slate-900">{svc.name}</div>
                    <div className="text-[10px] text-slate-400 font-mono">{svc.slug}</div>
                    {svc.help_text && <div className="text-[11px] text-slate-500 mt-0.5 max-w-md">{svc.help_text}</div>}
                  </td>
                  <td className="py-2 px-3">
                    <strong>{svc.min_delay_value}</strong> <span className="text-xs text-slate-500">{UNIT_LABEL[svc.min_delay_unit] || "días"}</span>
                  </td>
                  <td className="py-2 px-3 text-xs">{svc.duration_minutes} min</td>
                  <td className="py-2 px-3 text-xs">
                    {(svc.centers || []).map(c => (
                      <span key={c} className="inline-block bg-slate-100 rounded-sm px-1.5 py-0.5 mr-1 mb-0.5 uppercase">{c}</span>
                    ))}
                    {(svc.centers || []).length === 0 && <span className="text-slate-400">Todos</span>}
                  </td>
                  <td className="py-2 px-3 text-xs">
                    {svc.active ? <span className="text-green-700 font-semibold">Activo</span> : <span className="text-slate-400">Inactivo</span>}
                    {svc.archived && <span className="block text-[10px] text-orange-600">Archivado</span>}
                  </td>
                  <td className="py-2 px-3 text-right">
                    {canEdit && (
                      <div className="inline-flex gap-1.5">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(svc)} className="h-7 w-7 p-0" data-testid={`svc-edit-${svc.slug}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(svc)} className="h-7 w-7 p-0 text-red-600" data-testid={`svc-delete-${svc.slug}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Editor */}
      <Dialog open={!!editingId} onOpenChange={(o) => { if (!o) { setEditingId(null); setEditForm(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-sm" data-testid="svc-editor-dialog">
          <DialogHeader>
            <DialogTitle className="heading-industrial uppercase">
              {editingId === "new" ? "Nuevo servicio" : "Editar servicio"}
            </DialogTitle>
          </DialogHeader>
          {editForm && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Nombre público *</Label>
                  <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="svc-form-name" className="rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Slug interno</Label>
                  <Input value={editForm.slug} onChange={(e) => setEditForm({ ...editForm, slug: e.target.value })} placeholder="auto desde el nombre" data-testid="svc-form-slug" className="rounded-sm font-mono text-xs" />
                  <p className="text-[10px] text-slate-400 mt-0.5">No cambies si ya hay citas usando este slug.</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Plazo mínimo</Label>
                  <Input type="number" min="0" max="365" value={editForm.min_delay_value} onChange={(e) => setEditForm({ ...editForm, min_delay_value: parseInt(e.target.value || 0) })} data-testid="svc-form-delay-value" className="rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Unidad</Label>
                  <Select value={editForm.min_delay_unit} onValueChange={(v) => setEditForm({ ...editForm, min_delay_unit: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="svc-form-delay-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="business_days">Días laborables</SelectItem>
                      <SelectItem value="natural_days">Días naturales</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Duración (min)</Label>
                  <Input type="number" min="5" max="480" value={editForm.duration_minutes} onChange={(e) => setEditForm({ ...editForm, duration_minutes: parseInt(e.target.value || 60) })} data-testid="svc-form-duration" className="rounded-sm" />
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider">Centros donde se ofrece</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {centers.map(c => {
                    const checked = (editForm.centers || []).includes(c.slug);
                    return (
                      <label key={c.slug} className={`flex items-center gap-2 p-2 border rounded-sm cursor-pointer ${checked ? 'border-red-600 bg-red-50' : 'border-slate-200'}`}>
                        <input type="checkbox" checked={checked} onChange={(e) => {
                          const next = e.target.checked
                            ? [...(editForm.centers || []), c.slug]
                            : (editForm.centers || []).filter(x => x !== c.slug);
                          setEditForm({ ...editForm, centers: next });
                        }} data-testid={`svc-form-center-${c.slug}`} />
                        <span className="text-sm">{c.name}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-[10px] text-slate-400 mt-1">Si dejas todos desmarcados, el servicio aparece en todos los centros.</p>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider">Texto de ayuda (opcional)</Label>
                <Textarea value={editForm.help_text} onChange={(e) => setEditForm({ ...editForm, help_text: e.target.value })} placeholder="Aparece debajo del nombre en la web pública." data-testid="svc-form-help" className="rounded-sm" />
              </div>

              <div className="grid grid-cols-3 gap-3 pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch checked={editForm.active} onCheckedChange={(v) => setEditForm({ ...editForm, active: v })} data-testid="svc-form-active" />
                  <span className="text-sm">Activo</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch checked={editForm.requires_call} onCheckedChange={(v) => setEditForm({ ...editForm, requires_call: v })} data-testid="svc-form-requires-call" />
                  <span className="text-sm">Requiere llamada</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch checked={editForm.allows_replacement_vehicle} onCheckedChange={(v) => setEditForm({ ...editForm, allows_replacement_vehicle: v })} data-testid="svc-form-replacement" />
                  <span className="text-sm">Permite vehículo de alquiler</span>
                </label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditingId(null); setEditForm(null); }}>Cancelar</Button>
            <Button onClick={save} className="bg-slate-900 text-white" data-testid="svc-form-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmación de borrado */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <DialogContent className="rounded-sm" data-testid="svc-confirm-delete">
          <DialogHeader>
            <DialogTitle className="text-red-600 uppercase">Eliminar servicio</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-700">
            ¿Seguro que quieres eliminar <strong>{confirmDelete?.name}</strong>?
          </p>
          <p className="text-xs text-slate-500">
            Si hay citas históricas con este servicio, NO se borrará físicamente. Solo se desactivará y archivará. Las citas se conservan tal cual.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancelar</Button>
            <Button onClick={doDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="svc-confirm-delete-btn">Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
